import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaCloudUploadAlt, FaMusic, FaImage, FaSpinner, FaCheck, FaTimes, FaClock, FaTrash, FaLink, FaFolderOpen, FaPlay, FaEye } from 'react-icons/fa';
import '../styles/SubmitPage.css';

const API = process.env.REACT_APP_API_URL || '';
const AUDIO_EXTS = ['.mp3', '.m4a', '.flac', '.wav', '.ogg', '.aac', '.wma'];
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.webp', '.bmp'];

function SubmitPage({ user }) {
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [tab, setTab] = useState(isAdmin ? 'review' : 'submit');

  // Submit state
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('Disney');
  const [category, setCategory] = useState('film_soundtrack');
  const [audioFiles, setAudioFiles] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverMode, setCoverMode] = useState('file');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const dropRef = useRef();

  // Admin review state
  const [pendingAlbums, setPendingAlbums] = useState([]);
  const [reviewAlbum, setReviewAlbum] = useState(null);
  const [reviewTracks, setReviewTracks] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [loadingReview, setLoadingReview] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const audioPreviewRef = useRef();

  useEffect(() => {
    if (isAdmin) loadPending();
    loadMySubmissions();
  }, []);

  // ===== ADMIN FUNCTIONS =====
  const loadPending = async () => {
    try {
      const res = await axios.get(`${API}/api/submissions/pending`);
      setPendingAlbums(res.data);
    } catch (e) {}
  };

  const loadReviewTracks = async (albumId) => {
    try {
      const res = await axios.get(`${API}/api/albums/${albumId}`);
      setReviewTracks(res.data.tracks || []);
    } catch (e) {}
  };

  const openReview = (album) => {
    setReviewAlbum(album);
    setRejectReason('');
    loadReviewTracks(album.id);
  };

  const approveAlbum = async (id) => {
    if (!window.confirm('Approuver cet album ?')) return;
    setLoadingReview(true);
    try {
      await axios.put(`${API}/api/submissions/${id}/approve`);
      setPendingAlbums(prev => prev.filter(a => a.id !== id));
      setReviewAlbum(null);
      setPreviewAudio(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setLoadingReview(false); }
  };

  const rejectAlbum = async (id) => {
    if (!rejectReason.trim()) return alert('Indiquez une raison');
    setLoadingReview(true);
    try {
      await axios.put(`${API}/api/submissions/${id}/reject`, { reason: rejectReason });
      setPendingAlbums(prev => prev.filter(a => a.id !== id));
      setReviewAlbum(null);
      setPreviewAudio(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setLoadingReview(false); }
  };

  const playPreview = (track) => {
    if (previewAudio === track.id) {
      audioPreviewRef.current?.pause();
      setPreviewAudio(null);
    } else {
      setPreviewAudio(track.id);
      setTimeout(() => {
        if (audioPreviewRef.current) {
          audioPreviewRef.current.src = `${API}${track.file_path}`;
          audioPreviewRef.current.play().catch(() => {});
        }
      }, 50);
    }
  };

  // ===== SUBMIT FUNCTIONS =====
  const loadMySubmissions = async () => {
    try {
      const res = await axios.get(`${API}/api/submissions/my`);
      setMySubmissions(res.data);
    } catch (e) {}
  };

  const getExt = (name) => {
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.substring(dot).toLowerCase() : '';
  };

  const processFiles = (files) => {
    const newAudio = [];
    let foundCover = null;
    for (const file of files) {
      const ext = getExt(file.name);
      if (AUDIO_EXTS.includes(ext)) newAudio.push(file);
      else if (IMAGE_EXTS.includes(ext) && !foundCover) foundCover = file;
    }
    if (newAudio.length > 0) setAudioFiles(prev => [...prev, ...newAudio]);
    if (foundCover && !coverFile && !coverPreview) {
      setCoverFile(foundCover);
      setCoverPreview(URL.createObjectURL(foundCover));
      setCoverMode('file');
    }
    if (!title && newAudio.length > 0) {
      const firstPath = newAudio[0].webkitRelativePath || newAudio[0].name;
      const parts = firstPath.split('/');
      if (parts.length > 1) setTitle(parts[0]);
    }
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget === dropRef.current) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const items = e.dataTransfer.items;
    if (!items) return;
    const allFiles = [];
    const readEntry = (entry) => new Promise((resolve) => {
      if (entry.isFile) { entry.file(file => { allFiles.push(file); resolve(); }); }
      else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readAll = (entries = []) => {
          reader.readEntries(async (batch) => {
            if (batch.length === 0) { await Promise.all(entries.map(e => readEntry(e))); resolve(); }
            else readAll([...entries, ...batch]);
          });
        };
        readAll();
      } else resolve();
    });
    const promises = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) promises.push(readEntry(entry));
      else { const file = items[i].getAsFile(); if (file) allFiles.push(file); }
    }
    await Promise.all(promises);
    processFiles(allFiles);
  };

  const handleFolderSelect = (e) => processFiles(Array.from(e.target.files));
  const handleAudioSelect = (e) => {
    const files = Array.from(e.target.files);
    setAudioFiles(prev => [...prev, ...files.filter(f => AUDIO_EXTS.includes(getExt(f.name)))]);
  };
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) { setCoverFile(file); setCoverPreview(URL.createObjectURL(file)); }
  };
  const removeAudio = (i) => setAudioFiles(prev => prev.filter((_, idx) => idx !== i));
  const clearAll = () => { setAudioFiles([]); setCoverFile(null); setCoverPreview(null); setCoverUrl(''); setTitle(''); setArtist('Disney'); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return setMessage({ type: 'error', text: 'Titre requis' });
    if (audioFiles.length === 0) return setMessage({ type: 'error', text: 'Au moins un fichier audio' });
    setUploading(true); setProgress(0); setMessage(null);
    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('artist', artist.trim());
    formData.append('category', category);
    audioFiles.forEach(f => formData.append('audio', f));
    if (coverMode === 'file' && coverFile) formData.append('cover', coverFile);
    else if (coverMode === 'url' && coverUrl.trim()) formData.append('coverUrl', coverUrl.trim());
    try {
      const res = await axios.post(`${API}/api/submissions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / p.total) * 100))
      });
      setMessage({ type: 'success', text: res.data.message });
      clearAll(); loadMySubmissions();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || "Erreur lors de l'envoi" });
    } finally { setUploading(false); }
  };

  const statusIcon = { pending: <FaClock className="status-pending" />, approved: <FaCheck className="status-approved" />, rejected: <FaTimes className="status-rejected" /> };
  const statusLabel = { pending: 'En attente', approved: 'Approuvé', rejected: 'Refusé' };
  const formatDuration = (s) => s ? `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}` : '--';

  return (
    <div className="submit-page">
      {isAdmin && (
        <div className="submit-tabs">
          <button className={tab === 'review' ? 'active' : ''} onClick={() => setTab('review')}>
            ✅ Validation {pendingAlbums.length > 0 && <span className="pending-badge">{pendingAlbums.length}</span>}
          </button>
          <button className={tab === 'submit' ? 'active' : ''} onClick={() => setTab('submit')}>📤 Proposer</button>
        </div>
      )}

      {/* ===== ADMIN REVIEW TAB ===== */}
      {tab === 'review' && isAdmin && (
        <div className="review-section">
          <h1>✅ Albums en attente de validation</h1>
          {pendingAlbums.length === 0 ? (
            <div className="review-empty">Aucun album en attente</div>
          ) : !reviewAlbum ? (
            <div className="pending-list">
              {pendingAlbums.map(a => (
                <div key={a.id} className="pending-card" onClick={() => openReview(a)}>
                  {a.cover_url && <img src={a.cover_url.startsWith('http') ? a.cover_url : `${API}${a.cover_url}`} alt="" className="pending-cover" />}
                  <div className="pending-info">
                    <div className="pending-title">{a.title}</div>
                    <div className="pending-artist">{a.artist}</div>
                    <div className="pending-meta">
                      Par <strong>{a.submitted_by_name}</strong> • {a.track_count} pistes • {new Date(a.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <button className="review-btn"><FaEye /> Examiner</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="review-detail">
              <button className="review-back" onClick={() => { setReviewAlbum(null); setPreviewAudio(null); }}>← Retour</button>
              <div className="review-header">
                {reviewAlbum.cover_url && (
                  <img src={reviewAlbum.cover_url.startsWith('http') ? reviewAlbum.cover_url : `${API}${reviewAlbum.cover_url}`} alt="" className="review-cover" />
                )}
                <div>
                  <h2>{reviewAlbum.title}</h2>
                  <p className="review-artist">{reviewAlbum.artist}</p>
                  <p className="review-meta">Catégorie : {reviewAlbum.category} • Soumis par : {reviewAlbum.submitted_by_name}</p>
                </div>
              </div>

              <div className="review-tracks">
                <h3><FaMusic /> Pistes ({reviewTracks.length})</h3>
                <audio ref={audioPreviewRef} onEnded={() => setPreviewAudio(null)} />
                {reviewTracks.map(t => (
                  <div key={t.id} className={`review-track ${previewAudio === t.id ? 'playing' : ''}`}>
                    <button className="preview-play-btn" onClick={() => playPreview(t)}>
                      {previewAudio === t.id ? '⏸' : <FaPlay />}
                    </button>
                    <span className="review-track-num">{t.track_number}</span>
                    <div className="review-track-info">
                      <div className="review-track-title">{t.title}</div>
                      <div className="review-track-artist">{t.artist}</div>
                    </div>
                    <span className="review-track-dur">{formatDuration(t.duration)}</span>
                  </div>
                ))}
              </div>

              <div className="review-actions">
                <button className="approve-btn" onClick={() => approveAlbum(reviewAlbum.id)} disabled={loadingReview}>
                  <FaCheck /> Approuver
                </button>
                <div className="reject-area">
                  <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Raison du refus..." />
                  <button className="reject-btn" onClick={() => rejectAlbum(reviewAlbum.id)} disabled={loadingReview}>
                    <FaTimes /> Refuser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== SUBMIT TAB ===== */}
      {tab === 'submit' && (
        <>
          <h1><FaCloudUploadAlt /> Proposer un album</h1>
          <p className="submit-info">Glissez-déposez un dossier complet ou ajoutez les fichiers manuellement. Un administrateur validera avant publication.</p>

          <div ref={dropRef} className={`drop-zone ${isDragging ? 'dragging' : ''} ${audioFiles.length > 0 ? 'has-files' : ''}`}
            onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>
            {audioFiles.length === 0 ? (
              <div className="drop-zone-content">
                <FaFolderOpen className="drop-icon" />
                <p>Glissez un dossier ici</p>
                <span>ou</span>
                <div className="drop-buttons">
                  <label className="drop-btn"><FaFolderOpen /> Choisir un dossier<input type="file" webkitdirectory="true" directory="true" onChange={handleFolderSelect} hidden /></label>
                  <label className="drop-btn secondary"><FaMusic /> Fichiers audio<input type="file" accept=".mp3,.m4a,.flac,.wav,.ogg,.aac,.wma" multiple onChange={handleAudioSelect} hidden /></label>
                </div>
              </div>
            ) : (
              <div className="drop-zone-files">
                <div className="drop-zone-header">
                  <span><FaMusic /> {audioFiles.length} piste{audioFiles.length > 1 ? 's' : ''}</span>
                  <div>
                    <label className="add-more-btn">+ Ajouter<input type="file" accept=".mp3,.m4a,.flac,.wav,.ogg,.aac,.wma" multiple onChange={handleAudioSelect} hidden /></label>
                    <button className="clear-all-btn" onClick={clearAll}><FaTrash /></button>
                  </div>
                </div>
                <div className="audio-file-list">
                  {audioFiles.map((f, i) => (
                    <div key={i} className="audio-file-item">
                      <FaMusic className="audio-file-icon" />
                      <span className="audio-file-name">{f.name}</span>
                      <span className="audio-file-size">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button type="button" onClick={() => removeAudio(i)}><FaTimes /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="submit-form">
            <div className="submit-row">
              <div className="submit-cover-area">
                <div className="cover-mode-tabs">
                  <button type="button" className={coverMode === 'file' ? 'active' : ''} onClick={() => setCoverMode('file')}><FaImage /> Fichier</button>
                  <button type="button" className={coverMode === 'url' ? 'active' : ''} onClick={() => setCoverMode('url')}><FaLink /> Lien</button>
                </div>
                {coverMode === 'file' ? (
                  <label className="cover-upload-label">
                    {coverPreview ? <img src={coverPreview} alt="Cover" /> : <div className="cover-placeholder"><FaImage /><span>Pochette</span></div>}
                    <input type="file" accept="image/*" onChange={handleCoverChange} hidden />
                  </label>
                ) : (
                  <div className="cover-url-input">
                    <input type="url" value={coverUrl} onChange={e => setCoverUrl(e.target.value)} placeholder="https://..." />
                    {coverUrl && <img src={coverUrl} alt="" className="cover-url-preview" onError={e => e.target.style.display='none'} />}
                  </div>
                )}
              </div>
              <div className="submit-fields">
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre de l'album *" required />
                <input type="text" value={artist} onChange={e => setArtist(e.target.value)} placeholder="Artiste" />
                <select value={category} onChange={e => setCategory(e.target.value)}>
                  <option value="film_soundtrack">BO de Film</option>
                  <option value="disney_park">Disney Park</option>
                  <option value="public_album">Album Grand Public</option>
                </select>
              </div>
            </div>
            {message && <div className={`submit-message ${message.type}`}>{message.text}</div>}
            {uploading && <div className="submit-progress"><div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div><span>{progress}%</span></div>}
            <button type="submit" className="submit-btn" disabled={uploading || audioFiles.length === 0}>
              {uploading ? <><FaSpinner className="spin" /> Envoi...</> : <><FaCloudUploadAlt /> Soumettre ({audioFiles.length} piste{audioFiles.length > 1 ? 's' : ''})</>}
            </button>
          </form>

          {mySubmissions.length > 0 && (
            <div className="my-submissions">
              <h2>Mes soumissions</h2>
              <div className="submissions-list">
                {mySubmissions.map(s => (
                  <div key={s.id} className={`submission-item ${s.status}`}>
                    {s.cover_url && <img src={s.cover_url.startsWith('http') ? s.cover_url : `${API}${s.cover_url}`} alt="" className="submission-cover" />}
                    <div className="submission-info">
                      <div className="submission-title">{s.title}</div>
                      <div className="submission-artist">{s.artist}</div>
                      <div className="submission-date">{new Date(s.created_at).toLocaleDateString('fr-FR')}</div>
                    </div>
                    <div className="submission-status">{statusIcon[s.status]} {statusLabel[s.status]}</div>
                    {s.reject_reason && s.status === 'rejected' && <div className="submission-reason">Raison : {s.reject_reason}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SubmitPage;
